import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilState, useRecoilValueLoadable, useResetRecoilState, useSetRecoilState } from 'recoil';
import { EnumDepositOption, ObeService } from 'api';
import { getAgreementsRequestIdState } from 'api/agreements/getAgreementsAndVehicle';
import { isObeBeingMoved as isObeBeingMovedSelector } from 'api/obes/getIsObeBeingMoved';
import { getObeDetailsRequestIdState } from 'api/obes/getObeDetails';
import { getInactiveObesForCustomer } from 'api/obes/getObesForCustomer';
import EnumObeStatus from 'constants/obeStatuses';
import tagOptions from 'constants/tagOptions';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import { UNREGISTER_VEHICLE_SUMMARY } from 'routes/overview/UnregisterTag/paths';
import { inactiveTagToTerminate } from 'state/terminateInactiveTag';
import checkLoadable from 'utils/recoil/checkLoadable';
export default function useTerminateInactiveTag() {
  const [tag, setTag] = useRecoilState(inactiveTagToTerminate);
  const resetTag = useResetRecoilState(inactiveTagToTerminate);
  const [isLoading, setIsLoading] = useState(false);
  const setRequestId = useSetRecoilState(getAgreementsRequestIdState);
  const setObeDetailsRequestState = useSetRecoilState(getObeDetailsRequestIdState);
  const {
    customer
  } = useCurrentCustomer();
  const inactiveObesLoadable = useRecoilValueLoadable(getInactiveObesForCustomer(customer?.id));
  const {
    loading: obesLoading,
    contents: inactiveObes
  } = checkLoadable(inactiveObesLoadable);
  const isObeBeingMovedLoadable = useRecoilValueLoadable(isObeBeingMovedSelector(inactiveObes?.find(o => o.obeNumber === tag)?.obeId));
  const {
    contents: isObeBeingMoved
  } = checkLoadable(isObeBeingMovedLoadable);
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    t
  } = useTranslation();
  const {
    push
  } = useHistory();
  const reset = useCallback(() => {
    resetTag();
  }, [resetTag]);
  const terminateInactiveTag = async () => {
    if (!tag) return;
    try {
      setIsLoading(true);
      await ObeService.terminateInactiveObe({
        body: {
          obeNumber: tag,
          obeStatus: EnumObeStatus.Terminated,
          depositOption: EnumDepositOption.AwaitReturn
        }
      });
      resetTag();
      setRequestId(prev => prev + 1);
      setObeDetailsRequestState(prev => prev + 1);
      push(UNREGISTER_VEHICLE_SUMMARY, {
        tagOption: tagOptions.THROW_AND_RECEIVE_DEPOSIT
      });
    } catch (e) {
      enqueueSnackbar(t('error.terminate_inactive_tag'), {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    return reset;
  }, [reset]);
  const isValid = inactiveObes?.some(o => o.obeNumber === tag) && !isObeBeingMoved;
  return {
    tag,
    setTag,
    isLoading,
    terminateInactiveTag,
    isValid,
    reset,
    isObeBeingMoved,
    inactiveObes,
    obesLoading
  };
}